import {ScannedFields} from "../../../../../views/DocumentDrawer/types/Document.ts";
import {Money} from "../../../../../lib/Money.ts";
import {BankAccountType} from "../../../../../api/customerBankAccounts/CustomerBankAccount.ts";
import type {DocumentCategory, DocumentDirection} from '../../../../../generated/enums.ts';

export enum SortOptionsEnum {
    NONE = 'none',
    DATE_ASC = 'dateAsc',
    DATE_DESC = 'dateDesc',
    AMOUNT_ASC = 'amountAsc',
    AMOUNT_DESC = 'amountDesc',
}

export enum RenderTypeEnum {
    TRANSACTION_MATCH_PART = 'transaction_match_part',
    BILL_MATCH_PART = 'bill_match_part',
    PARTIAL_MATCH = 'partial_match',
    OPEN_BILL = 'open_bill',
    MISSING_DOCUMENT_REQUEST = 'missing_document_request',
}

export interface SortOption {
    value: SortOptionsEnum;
    label: string;
}

export interface CreditCardStatementLine {
    key: number;
    relationName: string;
    description: string;
    amount?: Money;
    bookingDate: string;
}

export interface CreditCardStatementDocumentOption {
    id: string;
    fileName: string;
    createdAt: string;
}

export interface MissingDocumentRequest {
    transaction_id: string;
    desc: string;
    relation_name: string;
    relation_iban: string;
    total_amount_payable: Money;
    booking_date: string; // ISO datetime string
    bank_account_iban: string | null;
    bank_account_owner_name: string;
    bank_account_type: BankAccountType;
    credit_card_number_suffix?: string | null;
    institution_name: string;
    bank_account_currency: string;
    business_name: string;
    bank_logo_url: string;
    general_ledger_account_id?: string | null;
    render_type: RenderTypeEnum.MISSING_DOCUMENT_REQUEST;
    nrs_amount: number | null;
}

export interface OpenBill {
    document_id: string;
    file_name: string;
    file_type: string;
    business_id: string;
    business_name: string;
    bill_id: string;
    document_category: DocumentCategory | null;
    document_direction: DocumentDirection;
    scanned_fields?: ScannedFields | null;
    render_type: RenderTypeEnum.OPEN_BILL;
    nrs_amount: number | null;
}

export interface MatchTransaction {
    transaction_id: string;
    match_id: string;
    is_reconciled: boolean;
    business_id: string;
    business_name: string;
    signed_amount: Money;
    bank_logo_url: string;
    bank_account_name: string;
    desc: string;
    booking_date: string;
    bank_account_iban?: string | null;
    bank_account_owner_name?: string | null;
    institution_name?: string | null;
    bank_account_currency?: string | null;
    relation_name?: string | null;
    relation_iban?: string | null;
    general_ledger_account_id?: string | null;
    nrs_amount: number | null;
}

export interface MatchBill {
    bill_id: string;
    match_id: string;
    is_reconciled: boolean;
    business_id: string;
    business_name: string;
    signed_amount: Money;
    relation_name: string;
    reference_identifier: string;
    document_id: string;
    normalized_date: string;
    file_name: string;
    file_type?: string | null;
    document_direction: DocumentDirection;
    nrs_amount: number | null;
}

export interface PartialMatch {
    match_id: string;
    business_id: string;
    business_name: string;
    bill_direction: DocumentDirection | null;
    transactions: MatchTransaction[];
    bills: MatchBill[];
    nrs_remainder: number | null;
    remainder: Money;
    is_reconciled: boolean;
    render_type: RenderTypeEnum.PARTIAL_MATCH;
}

export interface TransactionMatchPart {
    transaction_id: string;
    match_id: string;
    desc: string;
    relation_name: string;
    relation_iban: string;
    total_amount_payable: Money;
    booking_date: string; // ISO datetime string
    business_id: string;
    business_name: string;
    bank_account_iban: string | null;
    bank_account_owner_name: string;
    bank_account_type: BankAccountType;
    credit_card_number_suffix?: string | null;
    institution_name: string;
    bank_account_currency: string;
    bank_logo_url: string;
    matched_document_id: string | null;
    general_ledger_account_id?: string | null;
    render_type: RenderTypeEnum.TRANSACTION_MATCH_PART;
    nrs_amount: number | null;
}

export interface BillMatchPart {
    document_id: string;
    match_id: string;
    file_name: string;
    file_type: string;
    business_id: string;
    business_name: string;
    bill_id: string;
    document_category: DocumentCategory | null;
    document_direction: DocumentDirection;
    scanned_fields?: ScannedFields | null;
    render_type: RenderTypeEnum.BILL_MATCH_PART;
    nrs_amount: number | null;
}

export type TransactionWithMatchMetadata = MissingDocumentRequest | TransactionMatchPart;

export type BillDocument = OpenBill | BillMatchPart;

export type BillWithMatchMetadata = BillDocument | PartialMatch;

export function isPartialMatch(document: BillWithMatchMetadata): document is PartialMatch {
    return document.render_type === RenderTypeEnum.PARTIAL_MATCH;
}

export function isBillDocument(document: BillWithMatchMetadata): document is BillDocument {
    return document.render_type !== RenderTypeEnum.PARTIAL_MATCH;
}

export interface BusinessReconciliationOverview {
    business_id: string;
    business_name: string;
    transaction_side: TransactionWithMatchMetadata[];
    document_side: BillWithMatchMetadata[];
}

export enum CashFlowDirectionEnum {
    INFLOW = 'inflow',
    OUTFLOW = 'outflow',
}

export interface MatchTransactionPart {
    id: string;
    date: string;
    relation_name: string | null;
    description: string;
    bank_account_name: string;
    bank_logo_url: string;
    amount: number;
    flow: CashFlowDirectionEnum;
    currency: string;
}